import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Text } from '../text';
import { AsignadosBubbles } from '../AsignadoBubble';
import { useOrdenesTrabajoRaw } from '../../contexts/DataContext';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import { useOtConfig } from '../../hooks/useOrdenesTrabajo';
import { tipoBadgeClass } from './OtTarjeta';
import {
  folioTexto, isAbierta, fasesDe, faseDe, nombreTipo, diasEnFase, estadoFase, faseEstaTerminada, normalizarAsignados, ordenarOts,
} from '../../utils/ordenesTrabajo';
import { ClipboardDocumentCheckIcon, CheckIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

/**
 * The open órdenes on a fiado, a contrato or a fianza, where the agente
 * already is (ClienteDetails, ContratoView, FianzaView). Renders nothing when
 * the add-on is off or nothing is open. Click goes to the orden.
 *
 * @param {Object} props
 * @param {string|null} [props.clienteId]
 * @param {string|null} [props.contratoId]
 * @param {string|null} [props.fianzaId]
 * @param {string} [props.titulo]
 * @param {Function} [props.onNavigate]  - called before leaving (close a modal)
 */
export function OtActivas({ clienteId = null, contratoId = null, fianzaId = null, titulo = 'Órdenes de trabajo activas', onNavigate }) {
  const navigate = useNavigate();
  const { otEnabled, ordenesTrabajo } = useOrdenesTrabajoRaw();
  const { miembros } = useOrgMiembros();
  const { config } = useOtConfig();

  const filas = useMemo(() => {
    if (!otEnabled) return [];
    return ordenarOts(ordenesTrabajo.filter((o) => {
      if (!isAbierta(o)) return false;
      if (fianzaId) return o.fianzaId === fianzaId;
      if (contratoId) return o.contratoId === contratoId;
      if (clienteId) return o.clienteId === clienteId;
      return false;
    }));
  }, [otEnabled, ordenesTrabajo, clienteId, contratoId, fianzaId]);

  if (!otEnabled || filas.length === 0) return null;

  const abrir = (ot) => {
    onNavigate?.();
    navigate(`/app/ordenes?ot=${ot.id}`);
  };

  return (
    <div className="space-y-2 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-2">
        <ClipboardDocumentCheckIcon className="w-4 h-4 text-azul-surelia" />
        <Text className="text-sm font-semibold text-azul-surelia">{titulo}</Text>
        <span className="rounded-full bg-azul-surelia/10 text-azul-surelia text-[11px] font-bold px-1.5">{filas.length}</span>
      </div>
      <div className="divide-y divide-zinc-100">
        {filas.map((ot) => {
          const fases = fasesDe(config, ot.tipo);
          const fase = faseDe(fases, ot.faseId);
          const estado = estadoFase(ot, fase);
          const terminada = faseEstaTerminada(ot);
          const dias = diasEnFase(ot);
          return (
            <button
              key={ot.id}
              type="button"
              onClick={() => abrir(ot)}
              className="w-full flex items-center gap-3 py-2 text-left hover:bg-zinc-50 rounded"
            >
              <span className="text-xs font-semibold tabular-nums text-azul-surelia">{folioTexto(ot.folio)}</span>
              <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', tipoBadgeClass(ot.tipo))}>{nombreTipo(config, ot.tipo)}</span>
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">{ot.titulo}</span>
              <span className={clsx('text-xs tabular-nums whitespace-nowrap', terminada ? 'text-emerald-700' : estado === 'vencida' ? 'text-red-600 font-medium' : estado === 'porVencer' ? 'text-amber-600' : 'text-zinc-500')}>
                {fase ? fase.nombre : 'Sin fase'}
                {fase?.requiereAprobacion && !terminada && <ShieldCheckIcon className="inline w-3 h-3 ml-1 -mt-0.5 text-amber-600" />}
                {terminada ? <CheckIcon className="inline w-3 h-3 ml-1 -mt-0.5" /> : ` · ${dias} d`}
              </span>
              <AsignadosBubbles uids={normalizarAsignados(ot)} miembros={miembros} size="sm" max={3} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default OtActivas;
